'use client';

import React, { useState, Suspense, useEffect, useRef } from 'react';
import logger from '@/lib/logger';
import { useRouter, useSearchParams } from 'next/navigation';
import { Shield, Video, FileText, ArrowRight, Activity, Mail, Hash, UserPlus, Phone, ArrowLeft, LogOut, User } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { getCitizenJoinPrefill, parseCitizenInviteInput } from '@/lib/citizenJoin';
import { ProductCompanyLockup } from '@/components/brand/EngageIQBrand';

// Wrapper component to handle useSearchParams in Suspense boundary
function CitizenLandingContent() {
 const router = useRouter();
 const searchParams = useSearchParams();
 const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();

 // Read callId from URL params (supports both current callId and legacy room links)
 const { callId: prefilledCallId, inviteToken: prefilledInviteToken } = getCitizenJoinPrefill(searchParams);
 const hasShareLink = !!prefilledCallId || !!prefilledInviteToken;

 const [callId, setCallId] = useState(prefilledCallId);
 const [inviteToken, setInviteToken] = useState(prefilledInviteToken);
 const [callIdError, setCallIdError] = useState('');
 const [isJoining, setIsJoining] = useState(false);

 // Logout handler
 const handleLogout = async () => {
 try {
 await fetch('/api/auth/logout', { method: 'POST' });
 router.refresh();
 } catch (error) {
 logger.error('CitizenPage', 'Logout failed', { error });
 }
 };

 // Anonymous report state (ALTCHA)
 const [honeypot, setHoneypot] = useState('');
 const [showAnonymousForm, setShowAnonymousForm] = useState(false);
 const [altchaPayload, setAltchaPayload] = useState<string | null>(null);
 const [altchaError, setAltchaError] = useState('');
 const [isCreatingAnonymousCall, setIsCreatingAnonymousCall] = useState(false);
 const altchaRef = useRef<HTMLElement | null>(null);

 const inviteSuffix = inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : '';

 useEffect(() => {
 if (!showAnonymousForm) {
 setAltchaPayload(null);
 setAltchaError('');
 return;
 }

 let cancelled = false;
 import('altcha')
 .catch(() => {
 if (!cancelled) {
 setAltchaError('Verification is unavailable. Please refresh and try again.');
 }
 });

 return () => {
 cancelled = true;
 };
 }, [showAnonymousForm]);

 useEffect(() => {
 const widget = altchaRef.current;
 if (!widget) return;

 const handleVerified = (event: Event) => {
 const detail = (event as CustomEvent<{ payload?: string }>).detail;
 if (detail?.payload) {
 setAltchaPayload(detail.payload);
 setAltchaError('');
 }
 };

 const handleStateChange = (event: Event) => {
 const detail = (event as CustomEvent<{ state?: string }>).detail;
 if (detail?.state === 'error') {
 setAltchaPayload(null);
 setAltchaError('Verification failed. Please try again.');
 }
 if (detail?.state === 'expired') {
 setAltchaPayload(null);
 setAltchaError('Verification expired. Please retry.');
 }
 };

 widget.addEventListener('verified', handleVerified as EventListener);
 widget.addEventListener('statechange', handleStateChange as EventListener);

 return () => {
 widget.removeEventListener('verified', handleVerified as EventListener);
 widget.removeEventListener('statechange', handleStateChange as EventListener);
 };
 }, [showAnonymousForm]);

 const handleCallIdSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setCallIdError('');

 const parsed = parseCitizenInviteInput(callId);
 const resolvedCallId = (parsed.callId || callId).trim();
 const resolvedToken = (parsed.inviteToken || inviteToken).trim();

 if (parsed.callId && parsed.callId !== callId) {
 setCallId(parsed.callId);
 }
 if (parsed.inviteToken) {
 setInviteToken(parsed.inviteToken);
 }

 if (!resolvedCallId) {
 setCallIdError('Please enter a call ID or invite link');
 return;
 }

 setIsJoining(true);
 // Navigate to call — inviteToken is optional (room IDs work on their own)
 const tokenSuffix = resolvedToken ? `?inviteToken=${encodeURIComponent(resolvedToken)}` : '';
 router.push(`/calls/${encodeURIComponent(resolvedCallId)}${tokenSuffix}`);
 };

 const handleAnonymousReport = async () => {
 // Check honeypot (bots fill hidden fields)
 if (honeypot) {
 setAltchaError('Verification failed. Please try again.');
 return;
 }

 if (!altchaPayload) {
 setAltchaError('Please complete the verification to continue.');
 const widget = altchaRef.current as { verify?: () => Promise<void> } | null;
 await widget?.verify?.();
 return;
 }

 setIsCreatingAnonymousCall(true);
 setAltchaError('');

 try {
 const response = await fetch('/api/call-anon-create', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 altchaPayload,
 honeypot,
 }),
 });

 if (!response.ok) {
 const data = await response.json().catch((e) => {
 logger.error('CitizenPage', 'JSON parse error', { e });
 return null;
 });
 const message = data?.error || 'Unable to start an anonymous call. Please try again.';
 setAltchaError(message);
 return;
 }

 const data = await response.json();
 const targetUrl = `/calls/${encodeURIComponent(data.callId)}?inviteToken=${encodeURIComponent(data.inviteToken)}`;
 router.push(targetUrl);
 } catch {
 setAltchaError('Unable to start an anonymous call. Please try again.');
 } finally {
 setIsCreatingAnonymousCall(false);
 }
 };

 return (
 <main className="min-h-screen bg-[#0a0a0a] text-white selection:bg-blue-500/30 overflow-x-hidden">
 {/* Background Ambience */}
 <div className="fixed inset-0 pointer-events-none">
 <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[128px]"/>
 <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px]"/>
 </div>

 {/* Back to Home Link */}
 <div className="relative z-10 w-full px-4 pt-4 md:pt-4">
 <a
 href="/"
 className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm group"
 >
 <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform"/>
 Back to Home
 </a>
 </div>

 {/* Hero Section */}
 <section className="relative z-10 flex flex-col items-center justify-center min-h-0 md:min-h-[85vh] px-4 text-center mt-0 md:-mt-8">
 {/* Badge — hidden on mobile to save space */}
 <div className="hidden md:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-emerald-200/80 backdrop-blur-md mb-8 animate-fade-in-up">
 <Activity size={14} />
 <span>Secure Reporting Portal</span>
 </div>

 <h1 className="text-2xl md:text-6xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60 pb-2 md:pb-4 mb-2 md:mb-4">
 File a Report
 </h1>

 <p className="hidden md:block max-w-xl mx-auto text-lg text-zinc-400 leading-relaxed mb-8">
 Choose how you&apos;d like to proceed. Register for an account to track your reports, or file anonymously.
 </p>

 {/* Share Link Banner - shown when user was redirected from a call link */}
 {hasShareLink && (
 <div className="w-full max-w-2xl mx-auto mb-8 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-lg bg-blue-500/20">
 <Phone className="text-blue-400 w-5 h-5"/>
 </div>
 <div className="text-left">
 <p className="text-blue-200 font-medium">You&apos;ve been invited to a call</p>
 <p className="text-blue-300/70 text-sm">Sign in to track your report, or join the call below</p>
 </div>
 </div>
 </div>
 )}

 {/* Three Options Grid — Join a Call first on mobile, center on desktop */}
 <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr] items-center gap-4 md:gap-6 w-full max-w-5xl mx-auto">
 {/* Option 1: Sign In / Register OR Welcome Back — 2nd on mobile, 1st on desktop */}
 <div className="order-2 md:order-1 p-4 md:p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-emerald-500/50 hover:bg-white/10 transition-all duration-300">
 <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 w-fit mx-auto">
 {user ? (
 <User className="text-emerald-400 w-8 h-8"/>
 ) : (
 <Mail className="text-emerald-400 w-8 h-8"/>
 )}
 </div>

 {isAuthLoading ? (
 <div className="text-center py-4">
 <div className="w-6 h-6 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-3"/>
 <p className="text-sm text-zinc-400">Checking account...</p>
 </div>
 ) : user ? (
 <>
 <h3 className="text-xl font-semibold mb-2">Welcome Back!</h3>
 <p className="text-sm text-zinc-400 mb-2">
 Signed in as <span className="text-emerald-400 font-medium">{user.email || user.user_metadata?.full_name || 'User'}</span>
 </p>
 <p className="text-xs text-zinc-500 mb-6">
 Not you?{' '}
 <button
 onClick={handleLogout}
 className="text-emerald-400 hover:text-emerald-300 underline"
 >
 Sign out
 </button>
 </p>
 <div className="flex flex-col gap-3">
 <Link
 href="/citizen/dashboard"
 className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 Go to Dashboard
 <ArrowRight size={16} />
 </Link>
 </div>
 </>
 ) : (
 <>
 <h3 className="text-xl font-semibold mb-2">Sign In / Register</h3>
 <p className="text-sm text-zinc-400 mb-6">
 Create an account to track your reports and receive updates
 </p>
 <div className="flex flex-col gap-3">
 <Link
 href={callId ? `/citizen/login?callId=${encodeURIComponent(callId)}${inviteSuffix}` : '/citizen/login'}
 className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 Sign In
 <ArrowRight size={16} />
 </Link>
 <Link
 href={callId ? `/citizen/register?callId=${encodeURIComponent(callId)}${inviteSuffix}` : '/citizen/register'}
 className="w-full px-4 py-3 border border-white/20 hover:bg-white/5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 <UserPlus size={16} />
 Create Account
 </Link>
 </div>
 </>
 )}
 </div>

 {/* Option 2: Join a Call — HERO card, 1st on mobile, center on desktop */}
 <div className="order-1 md:order-2 p-5 md:p-8 md:py-10 rounded-2xl bg-blue-950/40 border-2 border-blue-500/40 backdrop-blur-sm shadow-[0_0_40px_rgba(59,130,246,0.15)] hover:border-blue-400/60 hover:shadow-[0_0_60px_rgba(59,130,246,0.25)] transition-all duration-300">
 <div className="mb-5 p-4 rounded-xl bg-blue-500/15 w-fit mx-auto">
 <Hash className="text-blue-400 w-10 h-10"/>
 </div>
 <h3 className="text-2xl font-bold mb-3 text-blue-50">Join a Call</h3>
 <p className="text-base text-blue-200/70 mb-8">
 Paste your invite link or enter a call ID from an officer
 </p>
 <form onSubmit={handleCallIdSubmit} className="space-y-4">
 <input
 type="text"
 value={callId}
 onChange={(e) => {
 setCallId(e.target.value);
 setCallIdError('');
 }}
 placeholder="Paste invite link or call ID..."
 className="w-full px-4 py-3 md:px-5 md:py-4 bg-white/5 border border-blue-500/20 rounded-lg focus:outline-none focus:border-blue-400/60 focus:ring-1 focus:ring-blue-500/30 placeholder-blue-300/30 text-center font-mono tracking-wider text-base md:text-lg"
 />
 {callIdError && (
 <p className="text-blue-400 text-sm">{callIdError}</p>
 )}
 <button
 type="submit"
 disabled={isJoining}
 className="w-full px-4 py-3 md:px-5 md:py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold text-base md:text-lg transition-all duration-300 flex items-center justify-center gap-2"
 >
 {isJoining ? 'Joining...' : 'Join Call'}
 <ArrowRight size={18} />
 </button>
 </form>
 </div>

 {/* Option 3: Anonymous Report — 3rd on mobile and desktop */}
 <div className="order-3 p-4 md:p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-violet-500/50 hover:bg-white/10 transition-all duration-300">
 <div className="mb-4 p-3 rounded-xl bg-violet-500/10 w-fit mx-auto">
 <Video className="text-violet-400 w-8 h-8"/>
 </div>
 <h3 className="text-xl font-semibold mb-2">Anonymous Report</h3>
 <p className="text-sm text-zinc-400 mb-6">
 File a report without creating an account
 </p>

 {!showAnonymousForm ? (
 <button
 onClick={() => setShowAnonymousForm(true)}
 className="w-full px-4 py-3 border border-violet-500/50 text-violet-300 hover:bg-violet-500/10 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 Start Anonymous Report
 </button>
 ) : (
 <div className="space-y-3">
 {/* Honeypot - hidden from users, visible to bots */}
 <input
 type="text"
 name="website"
 value={honeypot}
 onChange={(e) => setHoneypot(e.target.value)}
 style={{ display: 'none' }}
 tabIndex={-1}
 autoComplete="off"
 aria-hidden="true"
 />

 <div className="rounded-lg border border-violet-500/20 bg-white/5 px-3 py-3">
 <p className="text-xs text-zinc-400 mb-2">Human verification</p>
 <altcha-widget
 ref={altchaRef}
 challengeurl="/api/captcha/altcha?action=anon-call"
 auto="onload"
 hidelogo={true}
 hidefooter={true}
 />
 {altchaPayload && (
 <p className="text-emerald-300 text-xs mt-2 text-center">Verified</p>
 )}
 </div>
 {altchaError && (
 <p className="text-violet-300 text-sm text-center">{altchaError}</p>
 )}
 <button
 onClick={handleAnonymousReport}
 disabled={isCreatingAnonymousCall}
 className="w-full px-4 py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 {isCreatingAnonymousCall ? 'Starting...' : 'Start Report'}
 <ArrowRight size={16} />
 </button>
 </div>
 )}
 </div>
 </div>

 <p className="mt-8 text-sm text-zinc-500">
 By filing a report, you agree to our <span className="text-zinc-400 underline decoration-zinc-700 cursor-pointer">Terms of Service</span>.
 </p>
 </section>

 {/* Features Grid */}
 <section className="relative py-24 px-4 bg-zinc-900/30 border-t border-white/5">
 <div className="">
 <div className="grid md:grid-cols-3 gap-8">
 <FeatureCard
 icon={<Video className="text-emerald-400"size={32} />}
 title="Video Intake"
 description="Speak naturally to our AI agent. The system will record your statement and ask relevant follow-up questions."
 />
 <FeatureCard
 icon={<Shield className="text-blue-400"size={32} />}
 title="Secure & Private"
 description="Your report is encrypted and securely transmitted directly to the department's records system."
 />
 <FeatureCard
 icon={<FileText className="text-violet-400"size={32} />}
 title="Instant Filing"
 description="No waiting or handling times. Get a case number immediately upon completion of your report."
 />
 </div>
 </div>
 </section>

 <footer className="flex flex-col items-center gap-3 py-12 text-center text-zinc-600 text-sm border-t border-white/5 relative z-10 bg-black">
 <ProductCompanyLockup productHeight={18} companyHeight={9} />
 <p>&copy; {new Date().getFullYear()} NeutralAiz. For emergencies, please dial 911.</p>
 </footer>
 </main>
 );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
 return (
 <div className="p-8 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/10 transition-all duration-300 backdrop-blur-sm group">
 <div className="mb-6 bg-white/5 w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
 {icon}
 </div>
 <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-emerald-200 transition-colors">{title}</h3>
 <p className="text-zinc-400 leading-relaxed">
 {description}
 </p>
 </div>
 );
}

// Main export with Suspense boundary for useSearchParams
export default function CitizenLanding() {
 return (
 <Suspense fallback={
 <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
 <div className="text-zinc-400">Loading...</div>
 </main>
 }>
 <CitizenLandingContent />
 </Suspense>
 );
}
